import {Alert, Dropdown, Flex, MenuProps, Modal, Spin} from 'antd'
import {FC, useEffect, useState} from 'react'
import {WordDto, WordList} from '../../shared/types/api'
import {useAppDispatch, useAppSelector} from '../../store/hooks'
import '../../styles/scss/word-item/worditem.scss'
import {fetchWords} from '../../store/words/extraReducers'
import {
    clearNotSavedWordsRoutine,
    setWordsRoutine,
    wordsDataSelector,
    wordsIsisLoadingStatusSelector
} from '../../store/words'
import WordsLangHeader from '../pages/mainpages/dictionary/words/WordsLangHeader'
import GenerateWordsButton from '../pages/mainpages/dictionary/words/GenerateWordsButton'
import WordUtils from '../utils/WordUtils'
import AddWordsDictionaryDialog from './AddWordsDictionaryDialog'
import GenerateWordsDictionaryDialog from './GenerateWordsDictionaryDialog'
import DateUtils from '../utils/DateUtils'
import AddAIWordsButton from '../pages/mainpages/dictionary/words/AddAIWordsButton'
import SwitchLanguagesButton from '../pages/mainpages/dictionary/words/SwitchLanguagesButton'
import Constants from "../../const/Constants";
import type {ItemType} from "antd/es/menu/interface";
import UpperCaseWordsButton from "../pages/mainpages/dictionary/words/UpperCaseWordsButton";
import WordItemV2 from "../pages/mainpages/dictionary/words/WordItemV2";
import HowCreateCards from "../pages/mainpages/dictionary/words/HowCreateCards";
import ButtonsSkeleton from "../pages/mainpages/dictionary/words/ButtonsSkeleton";

interface CardsDictionaryDialogProps {
    isOpen: boolean,
    canEditWordList: boolean,
    close: () => {},
    wordList: WordList
}

export const CardsDictionaryDialog: FC<CardsDictionaryDialogProps> = ({wordList, isOpen, canEditWordList, close}) => {

    const words = useAppSelector(wordsDataSelector)
    const wordIsLoading = useAppSelector(wordsIsisLoadingStatusSelector)
    const dispatch = useAppDispatch()
    const fetchWordsByWordListIdRedux = (id: number) => dispatch(fetchWords(id))

    const [showAddWordsModal, setShowAddWordsModal] = useState<boolean>(false)
    const [showGenerateWords, setShowGenerateWords] = useState<boolean>(false)
    const [dropDownBtmMenuItems, setDropDownBtmMenuItems] = useState<ItemType[]>([
        {
            key: '1',
            label: 'Добавить карточки по шаблону',
            disabled: true
        },
    ])


    const setWordsRedux = (w: Array<WordDto>) => dispatch(setWordsRoutine(w))

    const clearAndClose = () => {
        //@ts-ignore
        dispatch(clearNotSavedWordsRoutine())
        close()
    }

    /**
     * Загрузить все слова по id списка слов
     * @param id списка слов
     */
    const loadWords = (id: number) => {
        fetchWordsByWordListIdRedux(id)
    }

    useEffect(() => {
        if (wordList?.id) {
            loadWords(wordList.id as number)
        }
    }, [wordList?.id])


    const closeAddWordsModal = () => {
        setShowAddWordsModal(false)
    }

    const openAddWordsModal = () => {
        setShowAddWordsModal(true)
    }

    /**
     * Добавить новое слово
     */
    const addWord = () => {
        const newWord = WordUtils.createNewWord(wordList.id)
        setWordsRedux([...words, newWord])
    }

    const selectedWords = words.filter((w: WordDto) => w.listId === wordList?.id)
    const wordsCount = selectedWords.length
    const savedWordsCount = selectedWords.filter((sw: WordDto) => !WordUtils.isNewEntity(sw.id)).length

    const onMenuClick: MenuProps['onClick'] = (e) => {
        if (e.key === '1') {
            openAddWordsModal()
        }
    };

    useEffect(() => {
        const isVisible = wordList !== null && wordsCount < Constants.MAX_COUNT_WORDS_IN_WORD_LIST
        setDropDownBtmMenuItems([{
            key: '1',
            label: 'Добавить карточки по шаблону',
            disabled: !isVisible
        }])
    }, [wordList, wordsCount])

    const missingCards = 5 - selectedWords.length;

    const declineCard = (n: number) => {
        const k = n % 10, l = n % 100;
        if (k === 1 && l !== 11) return 'карточку';
        if (k >= 2 && k <= 4 && (l < 12 || l > 14)) return 'карточки';
        return 'карточек';
    };

    const alertMessage = `Для использования списка в упражнениях нужно иметь более 5 карточек. Добавьте ещё ${missingCards} ${declineCard(missingCards)}`;


    return (
        <Modal
            width={800}
            okButtonProps={{hidden: true}}
            cancelButtonProps={{hidden: true}}
            title={`${wordList.listName} (${selectedWords.length})`} onCancel={clearAndClose} open={isOpen}
        >
            <WordsLangHeader
                canEdit={canEditWordList}
                activeWordList={wordList}
                wordCount={selectedWords.length}
            />
            {wordIsLoading && <ButtonsSkeleton/>}
            {!wordIsLoading && <Flex gap='16px' justify={'space-between'} className='modal-word-card__button-area'>
                {canEditWordList && <>
                    <Flex>
                        <Dropdown.Button
                            onClick={addWord}
                            type={'primary'}
                            menu={{items: dropDownBtmMenuItems, onClick: onMenuClick}}>Добавить
                            карточку</Dropdown.Button>
                    </Flex>
                    <AddAIWordsButton
                        selectedCategory={wordList}
                        wordsAmount={savedWordsCount}
                    />
                    <GenerateWordsButton
                        selectedCategory={wordList}
                        wordsAmount={wordsCount}
                        showAddWordsModal={() => setShowGenerateWords(true)}
                    />
                    <Flex gap={16} justify={'flex-end'}>
                        <SwitchLanguagesButton
                            selectedCategory={wordList}
                            wordsAmount={wordsCount}
                        />
                        <UpperCaseWordsButton
                            selectedCategory={wordList}
                            wordsAmount={wordsCount}
                        />
                        <HowCreateCards/>
                    </Flex>
                </>}
            </Flex>}
            {wordList && wordList.sideOneLang === wordList.sideTwoLang && <Alert
                style={{ marginTop: 16 }}
                message={"У вас выбраны одинаковые языки для обеих сторон карточки"}
                type='warning'
            />}
            {wordIsLoading && <Flex style={{marginTop: 64, marginBottom: 64}} justify={'center'}> <Spin size={'large'}
                                                                                                        tip="Загружаем карточки...">
                <div
                    style={{width: 300}}
                />
            </Spin></Flex>}
            {!wordIsLoading && missingCards > 0 && <Alert
                style={{ marginTop: 16 }}
                message={alertMessage}
                description='Оптимально иметь 15-30 карточек в каждом списке. Максиум 100 карточек'
                type="warning"
            />}
            {!wordIsLoading && <div style={{marginTop: 16}}>
                {selectedWords.sort((a: WordDto, b: WordDto) => DateUtils.compareDesc(a.created, b.created)).map((word: WordDto) => (
                    <WordItemV2 key={word.id}
                                wordItem={word}
                                cardList={wordList}
                                canEditWord={canEditWordList}
                                loadWords={loadWords}/>
                ))}
            </div>}
            <AddWordsDictionaryDialog
                open={showAddWordsModal}
                wordsAmount={wordsCount}
                activeWordList={wordList}
                cancelWindow={closeAddWordsModal}
                loadWords={(id) => loadWords(wordList.id as number)}
            />
            <GenerateWordsDictionaryDialog
                open={showGenerateWords}
                activeWordList={wordList}
                cancelWindow={() => setShowGenerateWords(false)}
            />
        </Modal>
    )
}


